import React from 'react';
import cx from 'classnames';

import useUniqueId from '../utils/useUniqueId';
import Touchable from '../Touchable';
import Text from '../Text';

import styles from './Tabs.module.scss';

export interface Props {
  children: React.ReactElement<TabProps> | Array<React.ReactElement<TabProps>>;
  className?: Parameters<typeof cx>[0];
  fixed?: boolean;
  onChange?: (idx: number) => void;
  selectedIndex?: number;
}

export const Tabs: React.FC<Props> = ({
  children,
  className,
  fixed,
  onChange,
  selectedIndex,
}) => {
  // component can be a controlled component or can be stateful under the hood
  const isControlledComponent = onChange != null && selectedIndex != null;

  const [internalSelectedIndex, setInternalSelectedIndex] = React.useState(
    selectedIndex || 0,
  );
  const tabControls: Array<React.ReactElement> = [];
  const tabControlRefs = React.useRef<Array<HTMLButtonElement | null>>([]);
  const tabPanels: Array<React.ReactElement> = [];
  // a unique ID to use as a base for building IDs for each tab and panel
  const uniqueIdBase = useUniqueId('tabs');
  // allow component to be controlled component
  React.useEffect(() => {
    if (selectedIndex != null) {
      setInternalSelectedIndex(selectedIndex);
    }
  }, [selectedIndex]);

  // a11y keyboard controls
  const keys = {
    end: 35,
    home: 36,
    left: 37,
    right: 39,
  };

  const handleOnChange = React.useCallback(
    (newIdx: number, focus?: boolean) => {
      if (newIdx > tabControls.length - 1) {
        newIdx = 0;
      }
      if (newIdx < 0) {
        newIdx = tabControls.length - 1;
      }
      if (onChange != null) {
        onChange(newIdx);
      }
      // focus controls if navigating via keyboard
      if (focus && tabControlRefs.current[newIdx] != null) {
        tabControlRefs.current[newIdx]!.focus();
      }
      setInternalSelectedIndex(newIdx);
    },
    [setInternalSelectedIndex, onChange, tabControls.length],
  );

  // Handle keydown on tabs
  const keydownEventListener = React.useCallback(
    (event: KeyboardEvent) => {
      var key = event.keyCode;

      switch (key) {
        case keys.end:
          event.preventDefault();
          handleOnChange(tabControls.length - 1, true);
          break;
        case keys.home:
          event.preventDefault();
          handleOnChange(0, true);
          break;
      }
    },
    [handleOnChange, keys, tabControls.length],
  );

  // Handle keyup on tabs
  const keyupEventListener = React.useCallback(
    (event: KeyboardEvent) => {
      let newIdx = internalSelectedIndex;

      switch (event.keyCode) {
        case keys.left:
          newIdx = internalSelectedIndex - 1;
          break;
        case keys.right:
          newIdx = internalSelectedIndex + 1;
          break;
        default:
          return;
      }
      handleOnChange(newIdx, true);
    },
    [internalSelectedIndex, keys, handleOnChange],
  );

  // Add key event listeners to tab controls
  React.useEffect(() => {
    tabControlRefs.current.forEach(ref => {
      if (ref == null) {
        return;
      }
      ref.addEventListener('keydown', keydownEventListener);
      ref.addEventListener('keyup', keyupEventListener);
    });

    // make a copy of refs to satisfy react-hooks/exhaustive-deps
    const refsForCleanup = tabControlRefs.current;

    // clean up event listeners
    return () => {
      refsForCleanup.forEach(ref => {
        if (ref == null) {
          return;
        }
        ref.removeEventListener('keydown', keydownEventListener);
        ref.removeEventListener('keyup', keyupEventListener);
      });
    };
  }, [
    tabControlRefs,
    internalSelectedIndex,
    keyupEventListener,
    keydownEventListener,
  ]);

  // due to the rules of hooks, we loop imperatively here so we can generate a
  // single unique id for use in both the tab control and panel elements
  React.Children.forEach(children, (child, idx) => {
    if (!React.isValidElement<TabProps>(child)) {
      return;
    }
    const isSelected =
      (isControlledComponent ? selectedIndex : internalSelectedIndex) === idx;
    // e.g. tabs-unique-id-0-0
    const id = child.props.id || `${uniqueIdBase}-${idx}`;
    // e.g. tabs-unique-id-0-0-panel
    const panelId = `${id}-panel`;

    tabControls.push(
      <Touchable
        ref={(el: HTMLButtonElement) => {
          tabControlRefs.current[idx] = el;
        }}
        id={id}
        role='tab'
        key={id}
        className={cx(
          styles.tab,
          isSelected && styles.isSelected,
          child.props.className,
        )}
        aria-selected={isSelected}
        aria-controls={panelId}
        tabIndex={isSelected ? undefined : -1}
        onClick={() => handleOnChange(idx)}
      >
        <Text
          as='span'
          weight='semiBold'
          contrast='high'
          className={styles.text}
        >
          {child.props.title}
        </Text>
      </Touchable>,
    );

    tabPanels.push(
      <div
        id={panelId}
        className={cx(child.props.contentClassName)}
        hidden={!isSelected}
        role='tabpanel'
        key={panelId}
        aria-labelledby={id}
        // Disabling the a11y linter here is OK according to both the linter docs and w3 example
        // https://github.com/jsx-eslint/eslint-plugin-jsx-a11y/blob/master/docs/rules/no-noninteractive-tabindex.md#rule-details
        // https://www.w3.org/TR/wai-aria-practices-1.1/examples/tabs/tabs-1/tabs.html#sc1_label
        //
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex={0}
      >
        {child.props.children}
      </div>,
    );
  });

  return (
    <div className={cx(className)}>
      <div className={cx(styles.tabList, fixed && styles.fixed)} role='tablist'>
        {tabControls}
      </div>
      {tabPanels}
    </div>
  );
};

export interface TabProps {
  children: React.ReactNode;
  contentClassName?: Parameters<typeof cx>[0];
  className?: Parameters<typeof cx>[0];
  title: string;
  id?: string;
}

export const Tab: React.FC<TabProps> = ({ children, contentClassName }) => (
  <div className={cx(contentClassName)}>{children}</div>
);

Tabs.displayName = 'Tabs';
export default Tabs;
export { styles };
